import { createRef, lazy } from "react";
import { Routes, Route, Navigate, BrowserRouter } from "react-router-dom";
import { SnackbarProvider } from "notistack";

// Mui
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, StyledEngineProvider } from "@mui/material";

// Settings
import versionObj from "./version.json";

// Styles
import "./scss/App.scss";

// Custom components
import NavigationScroll from "./components/layout/NavigationScroll";
import Loadable from "./components/general/Loadable";
import themes from "./themes";
import VerifyCode from "./components/pages/auth/VerifyCode";

// Utils
import settings from "./settings.json";

// Hooks
import { useFetchUser } from "./hooks/useFetchUser";

// Interfaces
import { role } from "./interfaces/Base";

// Pages
const Home = Loadable(lazy(() => import("./components/pages/Home")));
const MainLayout = Loadable(lazy(() => import("./components/layout/MainLayout")));
const MinimalLayout = Loadable(lazy(() => import("./components/layout/MinimalLayout")));
const Login = Loadable(lazy(() => import("./components/pages/auth/Login")));
const Patient = Loadable(lazy(() => import("./components/pages/patients/User")));
const Patients = Loadable(lazy(() => import("./components/pages/patients/Users")));
const ForgotPassword = Loadable(lazy(() => import("./components/pages/auth/ForgotPassword")));
const ChangePassword = Loadable(lazy(() => import("./components/pages/auth/ChangePassword")));
const Admin = Loadable(lazy(() => import("./components/pages/personalTrainers/Admin")));
const Admins = Loadable(lazy(() => import("./components/pages/personalTrainers/Admins")));
const Habit = Loadable(lazy(() => import("./components/pages/habits/Habit")));
const Stats = Loadable(lazy(() => import("./components/pages/stats/Stats")));
const Stat = Loadable(lazy(() => import("./components/pages/stats/Stat")));

const PrivateRoute = ({ title, rolesAllowed = [], children }: any) => {
    const { currentUser } = useFetchUser();

    document.title = `${title} - ${settings.app.title}`;

    // Hack-ish code to return you to the page you were before refresh
    if (!currentUser) {
        if (!window.location.href.includes("/login")) {
            return <Login />;
        }
    }

    return currentUser && rolesAllowed.some((r: string) => currentUser.roles.includes(r as role)) ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ title, children }: any) => {
    document.title = `${title} - ${settings.app.title}`;
    return children;
};

const App: React.FC<any> = () => {
    console.log(`%cVersion: ${versionObj && versionObj.version}`, "color: #bada55");

    const snackbarRef: any = createRef();

    return (
        <StyledEngineProvider injectFirst>
            <SnackbarProvider
                style={{ margin: 2 }}
                ref={snackbarRef}
                maxSnack={3}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                autoHideDuration={2000}
            >
                <ThemeProvider theme={themes()}>
                    <CssBaseline />
                    <NavigationScroll>
                        <BrowserRouter>
                            <Routes>
                                <Route
                                    path="/"
                                    element={
                                        <PrivateRoute title="Home" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Home />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="users"
                                    element={
                                        <PrivateRoute title="Users" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Patients />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="user"
                                    element={
                                        <PrivateRoute title="User" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Patient />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="user">
                                    <Route path=":id">
                                        <Route
                                            path=":index"
                                            element={
                                                <PrivateRoute title="User" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                                    <MainLayout>
                                                        <Patient />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                    </Route>
                                </Route>

                                <Route
                                    path="admins"
                                    element={
                                        <PrivateRoute title="Admins" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Admins />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="admin"
                                    element={
                                        <PrivateRoute title="Admin" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Admin />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="admin`">
                                    <Route path=":id">
                                        <Route
                                            path=":index"
                                            element={
                                                <PrivateRoute title="Admin" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                                    <MainLayout>
                                                        <Admin />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                    </Route>
                                </Route>

                                <Route
                                    path="stat"
                                    element={
                                        <PrivateRoute title="Stat" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Stat />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                >
                                    <Route
                                        path=":id"
                                        element={
                                            <PrivateRoute title="Stat" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                                <MainLayout>
                                                    <Stat />
                                                </MainLayout>
                                            </PrivateRoute>
                                        }
                                    ></Route>
                                </Route>

                                <Route
                                    path="stats"
                                    element={
                                        <PrivateRoute title="Stats" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                            <MainLayout>
                                                <Stats />
                                            </MainLayout>
                                        </PrivateRoute>
                                    }
                                />

                                <Route path="habit">
                                    <Route
                                        path=":id"
                                        element={
                                            <PrivateRoute title="Habit" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                                <MainLayout>
                                                    <Habit />
                                                </MainLayout>
                                            </PrivateRoute>
                                        }
                                    />
                                </Route>

                                <Route path="habit">
                                    <Route path=":id">
                                        <Route
                                            path=":userId"
                                            element={
                                                <PrivateRoute title="Habit" rolesAllowed={["superAdmin", "mainAdmin"]}>
                                                    <MainLayout>
                                                        <Habit />
                                                    </MainLayout>
                                                </PrivateRoute>
                                            }
                                        />
                                    </Route>
                                </Route>

                                <Route
                                    path="login"
                                    element={
                                        <PublicRoute title="Login">
                                            <MinimalLayout>
                                                <Login />
                                            </MinimalLayout>
                                        </PublicRoute>
                                    }
                                />
                                <Route
                                    path="/forgotPassword"
                                    element={
                                        <PublicRoute title="Forgot password">
                                            <ForgotPassword />
                                        </PublicRoute>
                                    }
                                />
                                <Route
                                    path="/verify"
                                    element={
                                        <PublicRoute title="Verify Code">
                                            <VerifyCode />
                                        </PublicRoute>
                                    }
                                />
                                <Route
                                    path="/changePassword"
                                    element={
                                        <PublicRoute title="Change Password">
                                            <ChangePassword />
                                        </PublicRoute>
                                    }
                                />

                                <Route path="*" element={<Login />} />
                            </Routes>
                        </BrowserRouter>
                    </NavigationScroll>
                </ThemeProvider>
            </SnackbarProvider>
        </StyledEngineProvider>
    );
};

export default App;
